import Loader from "components/common/Loader";
import { useEffect, useState } from "react";
import { Outlet, useNavigate, useParams, useSearchParams } from "react-router-dom";

import { selectEventDetail } from "store/eventDetails/eventDetails.selectors";
import { fetchEventDetails } from "store/eventDetails/eventDetails.actions";
import { fetchEventList } from "store/eventList/eventList.actions";
import { selectEventList } from "store/eventList/eventList.selectors";
import {
  resetEventList,
} from "store/eventList/eventList.slice";
import { TEventTab } from "store/eventList/EventList.types";
import { useAppDispatch, useAppSelector } from "store/hooks";
import { StatusState } from "store/types";
import Pagination from "../../../../components/common/Pagination";
import EventDetail from "./EventDetail/EventDetail";
import EventsFilterBar from "./EventsFilterBar";
import EventsHeader from "./EventsHeader";
import EventsList from "./EventsList";
import LoadingSkeleton from "./LoadingSkeleton";
import {
  EventDetailBody,
  EventDetailWrapper,
  EventsColumn,
  EventsPageContainer,
  EventsWrapper,
  PinkBarContainer,
  UpcomingEventTitle
} from "./NewEvents.styles";
import NoEventsPlaceholder from "./NoEventsPlaceholder";
import RecommendedEventModal from "./RecommendedEventModal";
import RecommendedEventsSection from "./RecommendedEventsSection";
import { IEventData } from "types";
import { resetEventDetails } from "store/eventDetails/eventDetails.slice";
import { selectUserType } from "store/user/user.selectors";

const Events = () => {

  const { publicUserId } = useParams();
  const dispatch = useAppDispatch();
  const userType = useAppSelector((state) => state.user.user_type);
  const {
    data: events,
    pagination,
    status,
    error,
  } = useAppSelector(selectEventList);
  const {
    data: selectedEvent,
    status: detailStatus,
    error: detailError,
  } = useAppSelector(selectEventDetail);

  const [searchParams] = useSearchParams();
  const typeParam = searchParams.get("type") as TEventTab | null;
  const eventIdParam = searchParams.get("eventId") as string | null;

  const [selectedTab, setSelectedTab] = useState<TEventTab>(
    typeParam ?? TEventTab.UPCOMING
  );
  const limit = 5;
  const [pastCount, setPastCount] = useState<number>(0);
  const [upcomingCount, setUpcomingCount] = useState<number>(0);
  const [isDropdownOpen, setIsDropdownOpen] = useState<boolean>(false);
  const [selectedId, setSelectedId] = useState<number | null>(
    eventIdParam ? parseInt(eventIdParam) : null
  );

  const [imageErrors, setImageErrors] = useState<{ [key: number]: boolean }>(
    {}
  );
  const [bannerErrors, setBannerErrors] = useState<{ [key: number]: boolean }>(
    {}
  );
  const [searchText, setSearchText] = useState("");
  const [debouncedSearchText, setDebouncedSearchText] = useState("");
  const [showCreateEvent, setShowCreateEvent] = useState(false);
  const [editingEvent, setEditingEvent] = useState<IEventData | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    dispatch(resetEventList());
    dispatch(resetEventDetails());
  }, [selectedTab, dispatch]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchText(searchText);
    }, 1000);

    return () => clearTimeout(timer);
  }, [searchText]);

  useEffect(() => {
    if (!userType) return;
    dispatch(
      fetchEventList({
        page: 1,
        limit,
        typeEvents: selectedTab,
        searchText: debouncedSearchText,
        ...(publicUserId ? { publicUserId: Number(publicUserId) } : {}),
      })
    );
  }, [dispatch, userType, selectedTab, limit, debouncedSearchText, publicUserId]);

  useEffect(() => {
    if (status === StatusState.SUCCEEDED && events?.length > 0) {
      const firstId = events[0].id;
      setSelectedId(firstId);
      dispatch(resetEventDetails());
      dispatch(fetchEventDetails(firstId));
    }
  }, [status, events, dispatch]);

  useEffect(() => {
    if (selectedTab === TEventTab.PAST) setPastCount(events?.length ?? 0);
    if (selectedTab === TEventTab.UPCOMING) setUpcomingCount(events?.length ?? 0);
  }, [events, selectedTab]);

  const user_type = useAppSelector(selectUserType)

  const handleTabChange = (type: TEventTab) => {
    let paramsString = "";
    searchParams.set("type", type);
    searchParams.forEach((value, key) => {
      paramsString += `${key}=${key === "type" ? type : value}&`;
    });
    navigate(`?${paramsString.substring(0, paramsString.length - 1)}`);
    setSelectedTab(type);
    setIsDropdownOpen(false);
  };

  const handleSelectEvent = (id: number) => {
    setSelectedId(id);
    dispatch(resetEventDetails());
    dispatch(fetchEventDetails(id));
  };

  const handleOpenCreateEvent = () => {
    navigate("new");
  };

  const handleEditEvent = (event: IEventData) => {
    setEditingEvent(event);
    setShowCreateEvent(true);
  };

  const handleImageError = (eventId: number) => {
    setImageErrors((prev) => ({ ...prev, [eventId]: true }));
  };

  const handleBannerError = (eventId: number) => {
    setBannerErrors((prev) => ({ ...prev, [eventId]: true }));
  };
  const handlePageChange = (page: number) => {
    try {
      if (page !== pagination.current_page) {
        dispatch(fetchEventList({
          page,
          limit,
          typeEvents: selectedTab,
          searchText: debouncedSearchText,
          ...(publicUserId ? { publicUserId: Number(publicUserId) } : {}),
        }));
      }
    } catch (error) {
      console.error("Error fetching events:", error);
    }
  }

  if (!userType) {
    return <LoadingSkeleton />;
  }

  // if (showCreateEvent) {
  //   return ();
  // }
  return (
    <EventsPageContainer>
      <EventsHeader
        userType={userType}
        pastCount={pastCount | 0}
        upcomingCount={upcomingCount | 0}
        handleOpenCreateEvent={handleOpenCreateEvent}
        onSearchChange={(value) => setSearchText(value)}
        publicUserId={publicUserId ? Number(publicUserId) : undefined}
      />
      {Boolean(publicUserId) ?
        <UpcomingEventTitle>Upcoming Events</UpcomingEventTitle>
        :
        <PinkBarContainer>
          <EventsFilterBar
            selectedTab={selectedTab}
            isDropdownOpen={isDropdownOpen}
            toggleDropdown={() => setIsDropdownOpen((prev) => !prev)}
            handleTabChange={handleTabChange}
          />
        </PinkBarContainer>
      }

      {status === StatusState.LOADING && <LoadingSkeleton />}

      {status !== StatusState.LOADING && (status === StatusState.FAILED || events?.length === 0) && (
        <NoEventsPlaceholder
          message={
            error
              ? error
              : (Boolean(publicUserId)
                ? `This user has ${selectedTab} no events planned`
                : `You have no ${selectedTab} events planned`)
          }
        />
      )}

      {status === StatusState.SUCCEEDED && events?.length > 0 && (
        <EventsWrapper>
          <EventsColumn>
            <EventsList
              events={events}
              selectedEvent={selectedEvent}
              selectedId={selectedId}
              handleSelectEvent={handleSelectEvent}
              imageErrors={imageErrors}
              handleImageError={handleImageError}
            />
            <Pagination
              blockSize={7}
              pagination={pagination}
              onPageChange={handlePageChange}
            />
          </EventsColumn>

          <EventDetailWrapper>
            {detailStatus === StatusState.LOADING && (
              <div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100%" }}>
                <Loader message="Loading event details..." />
              </div>
            )}
            {detailStatus === StatusState.FAILED && (
              <EventDetailBody style={{ color: "red", padding: "1rem" }}>
                {detailError}
              </EventDetailBody>
            )}

            {detailStatus === StatusState.SUCCEEDED && selectedEvent && (
              <EventDetail
                selectedEvent={selectedEvent}
                draftEvent={
                  selectedEvent.activity.condition_type === "Tentative" ||
                  selectedTab === "tentative"
                }
                userType={userType}
                bannerErrors={bannerErrors}
                handleBannerError={handleBannerError}
                onPublishClick={handleEditEvent}
              />
            )}
            {detailStatus === StatusState.SUCCEEDED &&
              !selectedEvent &&
              events?.length > 0 && (
                <EventDetailBody style={{ padding: "1rem" }}>
                  Select an event to see details
                </EventDetailBody>
              )}
          </EventDetailWrapper>
        </EventsWrapper>
      )}

      <RecommendedEventsSection />
      <RecommendedEventModal />

      <Outlet />
    </EventsPageContainer>
  );
};

export default Events;
